
import React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ArrowRight, CheckCircle2 } from "lucide-react"

export function Community() {
  const [email, setEmail] = useState("")
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (email) {
      setIsSubmitted(true)
    }
  }

  return (
    <section id="community" className="py-24 md:py-32 bg-cream">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Content */}
          <div className="space-y-8">
            <div>
              <p className="text-sm font-medium text-forest uppercase tracking-wider mb-4">
                Join the Movement
              </p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground mb-6 text-balance">
                Help us build something that truly belongs to your community
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Corkboard is being developed with communities, not just for them. Join our
                early access list to help shape the platform, and be the first to bring it
                to your neighbourhood.
              </p>
            </div>

            {/* Benefits List */}
            <ul className="space-y-4">
              {[
                "Early access to beta features",
                "Direct input on what we build",
                "Priority onboarding for your town",
                "Invites to community workshops",
              ].map((benefit) => (
                <li key={benefit} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-forest/10 text-forest flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <span className="text-foreground">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Signup Form */}
          <div className="bg-card rounded-3xl p-8 md:p-10 border border-border shadow-xl shadow-foreground/5">
            {!isSubmitted ? (
              <>
                <h3 className="font-serif text-2xl font-semibold text-foreground mb-2">
                  Join the waitlist
                </h3>
                <p className="text-muted-foreground mb-8">
                  Be among the first to experience Corkboard when we launch.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-secondary border border-border focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-all text-foreground placeholder:text-muted-foreground"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="postcode"
                      className="block text-sm font-medium text-foreground mb-2"
                    >
                      Postcode <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="postcode"
                      placeholder="CV8 1AB"
                      className="w-full px-4 py-3 rounded-xl bg-secondary border border-border focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none transition-all text-foreground placeholder:text-muted-foreground"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Helps us prioritise areas with the most interest.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      I am a... <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {["Resident", "Retailer", "Organiser", "Student", "Other"].map(
                        (role) => (
                          <label
                            key={role}
                            className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary border border-border cursor-pointer hover:border-forest/50 transition-colors has-[:checked]:bg-forest/10 has-[:checked]:border-forest"
                          >
                            <input
                              type="checkbox"
                              name="role"
                              value={role}
                              className="sr-only"
                            />
                            <span className="text-sm text-foreground">{role}</span>
                          </label>
                        )
                      )}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-forest hover:bg-forest-light text-cream rounded-xl h-14 text-base font-medium mt-4"
                  >
                    Join the Waitlist
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </form>

                <p className="text-xs text-muted-foreground text-center mt-6">
                  We respect your privacy. No spam, ever. Unsubscribe anytime.
                </p>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-forest/10 text-forest rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="font-serif text-2xl font-semibold text-foreground mb-3">
                  You&apos;re on the list!
                </h3>
                <p className="text-muted-foreground">
                  Thanks for joining. We&apos;ll be in touch soon with updates and early
                  access information.
                </p>
              </div>
            )}
          </div>
        </div>

      </div>
    </section>
  )
}
